import React from 'react';

import * as api from '@gitbook/api-client';
import { Icon } from '@gitbook/spine-icons';
import {
    Alert,
    Button,
    DataTable,
    type DataTableColumns,
    DataTableFooter,
    DataTableLayout,
    Description,
    DialogRoot,
    DialogTrigger,
    EmptyState,
    EmptyStateActions,
    EmptyStateIcon,
    Heading,
    type OverlayTriggerState,
    Pagination,
    Panel,
    useDialogTriggerState,
} from '@gitbook/spine-react';

import { apiResource, useSiteContext, useSuspensePaginatedAPIQuery } from '../../../../../state';
import { useSiteSettingsContext } from '../SiteSettingsContext';
import { AddMcpServerDialog } from './AddMcpServerDialog';
import { EditMcpServerDialog } from './EditMcpServerDialog';

export function SiteMcpServersPanel() {
    const addTrigger = useDialogTriggerState();
    const { organizationId, site } = useSiteContext();
    const { siteSettings } = useSiteSettingsContext();

    const canUseMCPServers = siteSettings.ai.mode === api.CustomizationAIMode.Assistant;

    const { data, range, next, previous } = useSuspensePaginatedAPIQuery(
        apiResource('listSiteMcpServers', { organizationId, siteId: site.id })
    );

    const columns: DataTableColumns<api.SiteMcpServer> = React.useMemo(
        () => [
            {
                key: 'name',
                label: 'Name',
                isRowHeader: true,
                cell: (row) => row.item.name,
            },
            {
                key: 'url',
                label: 'URL',
                cell: (row) => row.item.url,
            },
            {
                key: 'edit',
                label: '',
                width: 32, // Size of the button
                cell: (row) => (
                    <DialogRoot>
                        <DialogTrigger>
                            <Button
                                iconOnly
                                kind="blank"
                                leadingIcon={Icon.Edit}
                                label="Edit server"
                            />
                        </DialogTrigger>
                        <EditMcpServerDialog server={row.item} />
                    </DialogRoot>
                ),
            },
        ],
        []
    );

    return (
        <Panel>
            <Heading level={2}>Extend it with MCP connectors</Heading>
            {canUseMCPServers ? null : (
                <Alert
                    kind={data.items.length > 0 ? 'warning' : 'default'}
                    size="small"
                    title="MCP tools are only available for the Assistant experience"
                    details={
                        data.items.length > 0
                            ? 'Your configured MCP tools will NOT be used in the AI Search experience. You can switch to Assistant mode to use them.'
                            : 'You can only configure MCP tools when the Assistant experience is enabled.'
                    }
                />
            )}
            {data.items.length === 0 ? (
                <div className="rounded-lg border bg-base">
                    <SiteMcpServersEmptyState trigger={addTrigger} />
                </div>
            ) : (
                <>
                    <DataTableLayout>
                        <DataTable aria-label="MCP servers" columns={columns} items={data.items} />
                        <DataTableFooter>
                            <Pagination range={range} next={next} previous={previous} />
                        </DataTableFooter>
                    </DataTableLayout>
                    <div>
                        <DialogTrigger trigger={addTrigger}>
                            <Button
                                leadingIcon={Icon.Plus}
                                kind="secondary"
                                label="Add MCP server"
                            />
                        </DialogTrigger>
                    </div>
                </>
            )}
            <DialogRoot {...addTrigger.rootProps}>
                <AddMcpServerDialog />
            </DialogRoot>
        </Panel>
    );
}

function SiteMcpServersEmptyState({ trigger }: { trigger: OverlayTriggerState }) {
    return (
        <EmptyState>
            <EmptyStateIcon>
                <Icon.MCP />
            </EmptyStateIcon>
            <Heading>No MCP servers</Heading>
            <Description>
                Configure 3rd party tools that can be used by Assistant to generate answers.
            </Description>
            <EmptyStateActions>
                <DialogTrigger trigger={trigger}>
                    <Button leadingIcon={Icon.Plus} label="Configure MCP server" kind="secondary" />
                </DialogTrigger>
            </EmptyStateActions>
        </EmptyState>
    );
}
